/**
 * Hook for handling chat message sending logic.
 *
 * Handles:
 * - Building user message entries
 * - Creating new sessionless chats
 * - Sending follow-up messages to existing chats
 * - Analytics tracking
 */

import {
  chatStateAtom,
  sessionlessAttachmentIdsAtom,
  sessionlessFilesAtom,
  showSignUpModalAtom,
} from '@/atoms/chatAtoms';
import { evidencePowerUserBannerDismissedAtom } from '@/atoms/evidenceTrialQueryCountAtom';
import { resolveAttachableLinkedContext } from '@/components/chat/utils/resolveAttachableLinkedContext';

import type {
  ChatState,
  ErrorItemEntry,
  ReasoningItemEntry,
  SendMessageParams,
  TimelineEntry,
  UserMessageEntry,
} from '@/components/chat/types/types';
import { ChatMode } from '@/components/chat/types/types';
import { IS_EVIDENCE_SBS_ENABLED } from '@/features/flags/legacy-flags';
import {
  appendCheckpoint,
  finishTracking,
  startTracking,
  TrackingKey,
} from '@heidi/runtime/performance';
import type { WidgetMessage } from '@/lib/embed.utils';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  normalizeQueueDurationMs,
  toWireChipSurface,
  toWireEntryMethod,
  type ChatMessageSourcePayload,
  type ChipOrigin,
  type ChipSurface,
} from '@/models/analytics/features/chat';

import { getTempUserId, MAX_ANONYMOUS_MESSAGES_PER_CHAT } from '@/utils/tempUser';
import { isAxiosError } from 'axios';
import { ObjectId } from 'bson';
import { getDefaultStore, useAtomValue } from 'jotai';
import { useCallback, useRef } from 'react';
import { invalidateCurrentUser } from '@/hooks/query/useQueryUser';
import { TIMELINE_STREAM_ERROR } from '@/utils/chatErrors';
import { createOfflineQueryNetworkErrorEntry } from '@/components/chat/utils/evidenceOfflinePreSubmit';
import { resolveEvidenceNetworkFailure } from '@/utils/resolveEvidenceNetworkFailure';
import { generateId } from '@/utils/generateId';
import { getFileExtension } from '@/utils/fileHelpers';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { track } from '@/lib/analytics/track';
import { trackChatMessageSubmitted } from '@/lib/analytics/scribe/chat';

const SESSIONLESS_KEY = 'sessionless';

const getSendMessageErrorMessage = (error: unknown, fallback: string): string => {
  return error instanceof Error ? error.message : fallback;
};

const injectSessionlessNetworkError = (
  setChatState: (fn: (prev: ChatState) => ChatState) => void
): void => {
  setChatState((prev) => {
    const session = prev[SESSIONLESS_KEY];
    if (!session) return prev;

    const timelineWithoutPlaceholder = session.timeline.filter(
      (entry) => !(entry.type === 'reasoning_item' && entry.backendIndex === -1)
    );
    const errorEntry = createOfflineQueryNetworkErrorEntry(
      timelineWithoutPlaceholder.length
    );

    return {
      ...prev,
      [SESSIONLESS_KEY]: {
        ...session,
        timeline: [...timelineWithoutPlaceholder, errorEntry],
        streamingState: null,
        metadata: (() => {
          const { clientChatId: _discarded, ...metadataWithoutClientId } =
            session.metadata ?? {};
          return metadataWithoutClientId;
        })(),
      },
    };
  });
};

interface UseChatSendMessageParams {
  /** Current chat ID */
  chatId: string | null | undefined;
  /** Session ID (null if sessionless) */
  sessionId: string | null | undefined;
  /** Whether this is a sessionless chat */
  isSessionless: boolean;
  /** Canonical state key from useChat (avoids re-deriving the key format locally) */
  stateKey: string;
  /** Analytics session ID */
  analyticsSessionId: string;
  /** Effective chat mode */
  effectiveMode: ChatMode;
  /** Effective domains for evidence */
  effectiveDomains: string[] | undefined;
  /** Current conversation timeline */
  conversationTimeline: TimelineEntry[];
  /** Function to update chat state */
  setChatState: (fn: (prev: ChatState) => ChatState) => void;
  /** Function to start streaming */
  startStreaming: (
    timeline: TimelineEntry[],
    overrideMode?: ChatMode,
    onError?: (err: string) => void,
    chatId?: string | null,
    files?: File[],
    attachmentIds?: string[],
    /**
     * Analytics-only source metadata for the last user message. Forwarded
     * verbatim to the /chat/completions body for MongoDB persistence;
     * never used for routing/authorization.
     */
    source?: ChatMessageSourcePayload
  ) => Promise<void>;
  /** Function to create a new chat */
  createChat: (params: {
    chatId: string;
    firstMessage: string;
    sessionId: string | null;
    patientProfileId: string | null;
    /**
     * EVI-1680: when true, the created chat is adopted into the session sidebar
     * (no navigation to the standalone chat route). Used by the eager-create
     * path for new in-session chats so Evidence SBS setup can fire on message 1.
     */
    inSessionSidebar?: boolean;
    /** Create the chat without navigating to the standalone Evidence route. */
    stayOnPage?: boolean;
  }) => Promise<unknown>;
  /**
   * Called after a stay-on-page create succeeds, with the client chat id.
   * The host adopts that id so the stream starts here instead of after a redirect.
   */
  onHostedChatCreated?: (chatId: string) => void;
  /** Sessionless hosts that must not navigate to `/scribe/chat`. */
  stayOnPage?: boolean;
  /** Function to set error state */
  setError: (err: string | null) => void;
  /** Ref to track query length for analytics */
  lastQueryLengthRef: React.MutableRefObject<number>;
  /** Ref to store chip metadata for deferred message_received enrichment */
  lastChipMetaRef: React.MutableRefObject<{
    entryMethod?: string;
    chipLabel?: string;
    chipCategory?: string;
    chipOrigin?: ChipOrigin;
    chipSurface?: ChipSurface;
    queued?: boolean;
    queueDurationMs?: number;
  }>;
  /**
   * Remote kill switch for the linked-context provenance gate
   * (`op-evidence-disable-linked-context-provenance-gate`), resolved by the
   * composition root (Chat.tsx holds the quarantine pair to
   * `@/features/flags`; this hook must not add one) and threaded down.
   */
  isProvenanceGateKilled?: boolean;
}

interface UseChatSendMessageReturn {
  sendMessage: (params: SendMessageParams) => Promise<void>;
}

/**
 * Hook that provides the sendMessage function for chat functionality.
 */
export function useChatSendMessage({
  chatId,
  sessionId,
  isSessionless,
  stateKey,
  analyticsSessionId,
  effectiveMode,
  effectiveDomains,
  conversationTimeline,
  setChatState,
  startStreaming,
  createChat,
  setError,
  lastQueryLengthRef,
  lastChipMetaRef,
  isProvenanceGateKilled = false,
  onHostedChatCreated,
  stayOnPage = false,
}: UseChatSendMessageParams): UseChatSendMessageReturn {
  const { trackChatSubmitted, trackChatCreated, trackChatError } =
    useEvidenceAnalytics();
  const isEvidencePowerUserBannerDismissed = useAtomValue(
    evidencePowerUserBannerDismissedAtom
  );
  const store = getDefaultStore();
  // Closes the sync race where two sends fire before the first setChatState
  // lands — the atom read alone still sees an empty pending slice on send #2.
  const pendingCreateByKeyRef = useRef<Map<string, string>>(new Map());

  const sendMessage = useCallback(
    async ({
      content,
      files,
      attachmentIds,
      fileMetadata,
      overrideMode,
      entryMethod,
      chipLabel,
      chipCategory,
      chipOrigin,
      chipSurface,
      queued,
      queueDurationMs,
    }: SendMessageParams) => {
      const trimmedContent = content.trim();
      if (!trimmedContent) return;
      const modeToTrack = overrideMode ?? effectiveMode ?? 'evidence';
      const trackMessageSubmitted = () => {
        trackChatMessageSubmitted({
          sessionId: analyticsSessionId,
          mode: modeToTrack,
          message_length: trimmedContent.length,
        });
      };

      const timelineWithoutErrors = conversationTimeline.filter(
        (entry) => entry.type !== 'error_item'
      );

      // Block anonymous users who have reached the per-chat message limit.
      if (getTempUserId()) {
        const userMessageCount = timelineWithoutErrors.filter(
          (e) => e.type === 'user_message'
        ).length;
        if (userMessageCount >= MAX_ANONYMOUS_MESSAGES_PER_CHAT) {
          store.set(showSignUpModalAtom, true);
          return;
        }
      }

      const pendingStateKey = isSessionless ? SESSIONLESS_KEY : stateKey;
      const inFlightClientChatId =
        store.get(chatStateAtom)?.[pendingStateKey]?.metadata?.clientChatId ??
        pendingCreateByKeyRef.current.get(pendingStateKey) ??
        null;
      const effectiveChatId = chatId ?? inFlightClientChatId;

      // Linked-context ids this send may attach — read FRESH from the store
      // and provenance-gated, so context primed from another chat (the
      // backend-sync in useChatContextLinking) can never ride into this
      // chat's create call or analytics. Render-closure atom reads here used
      // to race the new-chat stale-context cleanup and stamp a freshly
      // created chat with the previously viewed session's patient. The gate
      // is remotely killable (raw-atom reads restored) via
      // `op-evidence-disable-linked-context-provenance-gate`.
      const attachableLinkedContext = resolveAttachableLinkedContext(
        store,
        effectiveChatId,
        { gateKilled: isProvenanceGateKilled }
      );

      const hasAttachmentsForPerf =
        (attachmentIds && attachmentIds.length > 0) || (files && files.length > 0);
      startTracking({
        key: TrackingKey.EVIDENCE_MESSAGE_SEND,
        metadata: {
          chat_id: chatId ?? null,
          session_id: sessionId ?? null,
          is_sessionless: isSessionless,
          entry_method: entryMethod ?? 'typed',
          message_length: trimmedContent.length,
          has_attachments: !!hasAttachmentsForPerf,
          has_linked_session: !!attachableLinkedContext.sessionId,
          has_linked_patient: !!attachableLinkedContext.patientProfileId,
          // Boolean only (no origin, no ids): staging existed but the
          // provenance gate refused it. The prod signal for both "the gate
          // is firing on carryover" and "the gate is over-refusing" — the
          // kill-switch decision input.
          linked_context_gated: attachableLinkedContext.refused,
          queued: queued ?? false,
        },
      });

      const messageId = generateId();
      const userPosition = timelineWithoutErrors.length;

      // Notify the host page (when embedded) that a user message was
      // dispatched. Marketing listens for the first unauth message to fire a
      // Lead conversion in GTM (Google Ads + Meta), deduped by messageId.
      // No-op outside an iframe; standalone scribe is unaffected.
      try {
        if (typeof window !== 'undefined' && window.parent !== window) {
          const isAuthenticated = !getTempUserId();
          // Use !some(user_message) so a pre-loaded greeting/assistant entry
          // doesn't suppress the first genuine user submission.
          const isFirstMessage = !timelineWithoutErrors.some(
            (e) => e.type === 'user_message'
          );
          const message: WidgetMessage = {
            type: 'heidi:evidence_message_sent',
            isAuthenticated,
            isFirstMessage,
            messageId,
            sentAt: Date.now(),
          };
          // Restrict targetOrigin to the embedding parent when document.referrer
          // is available; payload carries auth state so '*' is a fallback only.
          let parentOrigin = '*';
          try {
            if (document.referrer) {
              parentOrigin = new URL(document.referrer).origin;
            }
          } catch {
            parentOrigin = '*';
          }
          window.parent.postMessage(message, parentOrigin);
        }
      } catch (e) {
        console.error('[Embed] Failed to post evidence_message_sent:', e);
      }

      // Build file metadata for timeline entry
      // Prefer fileMetadata (from pre-uploaded attachments) over raw files
      // Note: Use file extension (not MIME type) for `type` field to match UserMessage display logic
      const filesForTimeline = fileMetadata
        ? fileMetadata.map(
            (f: { name: string; url?: string; attachment_id?: string }) => ({
              name: f.name,
              type: getFileExtension(f.name),
              url: f.url,
              attachment_id: f.attachment_id,
            })
          )
        : files?.map((file) => ({
            name: file.name,
            type: getFileExtension(file.name),
          }));

      const userMessage: UserMessageEntry = {
        type: 'user_message',
        id: messageId,
        content: content.trim(),
        position: userPosition,
        timestamp: Date.now(),
        ...(filesForTimeline &&
          filesForTimeline.length > 0 && {
            files: filesForTimeline,
          }),
      };

      setError(null);

      const looksLikeFirstMessage = !chatId && timelineWithoutErrors.length === 0;

      const pendingState = store.get(chatStateAtom)?.[pendingStateKey];
      const isPendingSbsCreate =
        !isSessionless &&
        !!sessionId &&
        IS_EVIDENCE_SBS_ENABLED() &&
        (pendingCreateByKeyRef.current.has(pendingStateKey) ||
          pendingState?.metadata?.sbsPendingResume === true);

      // EVI-2358: absorb duplicate first-message sends while the in-session SBS
      // eager-create is still in flight — streaming is deferred to migration and
      // must not start here (unlike sessionless, where follow-up streaming is OK
      // once a clientChatId exists).
      if (isPendingSbsCreate && inFlightClientChatId && looksLikeFirstMessage) {
        return;
      }

      const isNewSessionlessChat =
        isSessionless && looksLikeFirstMessage && !inFlightClientChatId;

      if (isNewSessionlessChat) {
        // Declared outside the try so the catch can still emit it as the backend
        // join key when createChat fails (create_chat has no message_id yet).
        const clientChatId = new ObjectId().toHexString();

        pendingCreateByKeyRef.current.set(pendingStateKey, clientChatId);
        try {
          const modeToUse = overrideMode ?? effectiveMode ?? ChatMode.EVIDENCE;
          const now = Date.now();

          // Create a placeholder entry to show loader immediately
          const placeholderEntry: ReasoningItemEntry = {
            type: 'reasoning_item',
            id: generateId(),
            content: [],
            position: userPosition + 1,
            backendIndex: -1, // -1 indicates placeholder
          };

          // Store File objects for this message (atom persists across navigation)
          if (files && files.length > 0) {
            const filesAtom = sessionlessFilesAtom(userMessage.id);
            store.set(filesAtom, files);
          }

          // Store pre-uploaded attachment IDs for this message (atom persists across navigation)
          if (attachmentIds && attachmentIds.length > 0) {
            const attachmentIdsAtom = sessionlessAttachmentIdsAtom(userMessage.id);
            store.set(attachmentIdsAtom, attachmentIds);
          }

          setChatState((prev) => ({
            ...prev,
            [SESSIONLESS_KEY]: {
              timeline: [userMessage, placeholderEntry],
              streamingState: {
                basePosition: userPosition + 1,
                activeIndices: new Map(),
                currentActiveIndex: null,
                activityGroupOpen: true,
                streamStartedAt: now,
                lastChunkAt: now,
                lastVisibleContentAt: now,
              },
              metadata: {
                lastActivity: now,
                pendingMode: modeToUse,
                clientChatId,
              },
            },
          }));

          await createChat({
            chatId: clientChatId,
            firstMessage: trimmedContent,
            sessionId: attachableLinkedContext.sessionId,
            patientProfileId: attachableLinkedContext.patientProfileId,
            ...(stayOnPage ? { stayOnPage: true } : {}),
          });
          if (stayOnPage) {
            onHostedChatCreated?.(clientChatId);
          }

          appendCheckpoint({
            key: TrackingKey.EVIDENCE_MESSAGE_SEND,
            checkpointName: 'chat_created',
            metadata: {
              chat_id: clientChatId,
            },
          });

          // Track chat created event
          trackChatCreated(clientChatId, attachableLinkedContext.sessionId ?? undefined);
          trackMessageSubmitted();

          return;
        } catch (error) {
          pendingCreateByKeyRef.current.delete(pendingStateKey);
          const errorMsg = getSendMessageErrorMessage(error, 'Failed to create chat');
          const httpStatus = isAxiosError(error) ? error.response?.status : undefined;
          // create_chat fails pre-stream, so there's no message_id yet — but the
          // chat_id is FE-minted before the request, so emit it as the join key
          // to pivot into the retained backend POST /chat trace. http_status
          // distinguishes a rejected request (4xx/5xx) from an opaque transport
          // drop (undefined).
          finishTracking({
            key: TrackingKey.EVIDENCE_MESSAGE_SEND,
            metadata: {
              result: 'error',
              error_type: 'create_chat',
              chat_id: clientChatId,
              ...(httpStatus !== undefined && { http_status: httpStatus }),
            },
          });
          trackChatError({
            errorType: 'create_chat',
            errorMessage: errorMsg,
            httpStatus,
          });
          if (errorMsg === TIMELINE_STREAM_ERROR.USER_REF_BANNED) {
            setChatState((prev) => {
              const session = prev[SESSIONLESS_KEY];
              if (!session) return prev;

              const timelineWithoutPlaceholder = session.timeline.filter(
                (entry) => !(entry.type === 'reasoning_item' && entry.backendIndex === -1)
              );
              const errorEntry: ErrorItemEntry = {
                type: 'error_item',
                id: generateId(),
                content: TIMELINE_STREAM_ERROR.USER_REF_BANNED,
                position: timelineWithoutPlaceholder.length,
              };
              const { clientChatId: _discarded, ...metadataWithoutClientId } =
                session.metadata;

              return {
                ...prev,
                [SESSIONLESS_KEY]: {
                  ...session,
                  timeline: [...timelineWithoutPlaceholder, errorEntry],
                  streamingState: null,
                  metadata: metadataWithoutClientId,
                },
              };
            });
            return;
          }
          if (resolveEvidenceNetworkFailure(error)) {
            injectSessionlessNetworkError(setChatState);
            return;
          }
          // EVI-2358: drop the pending slice so a retry can createChat again — the
          // backend never received this chat_id (mirrors the SBS rollback path).
          setChatState((prev) => {
            if (!prev[SESSIONLESS_KEY]) return prev;
            const rest = { ...prev };
            delete rest[SESSIONLESS_KEY];
            return rest;
          });
          setError(errorMsg);
          return;
        } finally {
          pendingCreateByKeyRef.current.delete(pendingStateKey);
        }
      }

      // EVI-1680: a brand-new in-session chat has no chat_id until the backend
      // creates one mid-stream, so Evidence SBS setup (which requires a chat_id)
      // is skipped on the very first message — the experiment only ever starts
      // from message 2. For SBS-eligible users we eager-create the chat here
      // (mirroring the sessionless flow) so setup can fire on message 1. The
      // stream is deferred: createChat selects the chat in the session sidebar,
      // the chat component remounts under `chat-${clientChatId}`, and
      // useSessionlessChatMigration resumes the stream. Gated behind the SBS flag
      // so the lazy in-session creation path is unchanged for everyone else.
      const isNewInSessionSbsChat =
        !isSessionless &&
        looksLikeFirstMessage &&
        !inFlightClientChatId &&
        !!sessionId &&
        IS_EVIDENCE_SBS_ENABLED();

      if (isNewInSessionSbsChat) {
        // Declared outside the try so the catch can still emit it as the backend
        // join key when createChat fails (create_chat has no message_id yet).
        const clientChatId = new ObjectId().toHexString();

        pendingCreateByKeyRef.current.set(pendingStateKey, clientChatId);
        try {
          const modeToUse = overrideMode ?? effectiveMode ?? ChatMode.EVIDENCE;
          const now = Date.now();

          if (files && files.length > 0) {
            store.set(sessionlessFilesAtom(userMessage.id), files);
          }
          if (attachmentIds && attachmentIds.length > 0) {
            store.set(sessionlessAttachmentIdsAtom(userMessage.id), attachmentIds);
          }

          // stateKey here is `session-${sessionId}-new` (no chatId yet). The
          // `sbsPendingResume` marker scopes the deferred-stream resume to this
          // path only, so normal in-session chats are unaffected.
          //
          // streamingState is intentionally null: the real stream is started by
          // useSessionlessChatMigration once the chat is selected in the sidebar
          // and remounts under `chat-${clientChatId}`. A non-null optimistic
          // streaming state would make that resume think a stream is already
          // active (via `currentChatStreamingState`) and skip it. The optimistic
          // "thinking" loader is surfaced by useChat's startStreaming when the
          // deferred stream resumes (covering the /chat/sbs/setup window without
          // the removed SBS setup skeleton).
          setChatState((prev) => ({
            ...prev,
            [stateKey]: {
              timeline: [userMessage],
              streamingState: null,
              metadata: {
                lastActivity: now,
                pendingMode: modeToUse,
                clientChatId,
                sbsPendingResume: true,
              },
            },
          }));

          await createChat({
            chatId: clientChatId,
            firstMessage: trimmedContent,
            sessionId,
            // EVI-2500: this path is in-session only (`sessionId` is required
            // above). Never forward a leaked standalone patient with it.
            patientProfileId: null,
            inSessionSidebar: true,
          });

          appendCheckpoint({
            key: TrackingKey.EVIDENCE_MESSAGE_SEND,
            checkpointName: 'chat_created',
            metadata: { chat_id: clientChatId },
          });
          trackChatCreated(clientChatId, sessionId ?? undefined);
          trackMessageSubmitted();

          return;
        } catch (error) {
          pendingCreateByKeyRef.current.delete(pendingStateKey);
          const errorMsg = getSendMessageErrorMessage(error, 'Failed to create chat');
          const httpStatus = isAxiosError(error) ? error.response?.status : undefined;
          // create_chat fails pre-stream, so there's no message_id yet — but the
          // chat_id is FE-minted before the request, so emit it as the join key
          // to pivot into the retained backend POST /chat trace. http_status
          // distinguishes a rejected request (4xx/5xx) from an opaque transport
          // drop (undefined).
          finishTracking({
            key: TrackingKey.EVIDENCE_MESSAGE_SEND,
            metadata: {
              result: 'error',
              error_type: 'create_chat',
              chat_id: clientChatId,
              ...(httpStatus !== undefined && { http_status: httpStatus }),
            },
          });
          trackChatError({
            errorType: 'create_chat',
            errorMessage: errorMsg,
            httpStatus,
          });
          if (resolveEvidenceNetworkFailure(error)) {
            setChatState((prev) => ({
              ...prev,
              [stateKey]: {
                timeline: [
                  userMessage,
                  createOfflineQueryNetworkErrorEntry(userPosition + 1),
                ],
                streamingState: null,
                metadata: { lastActivity: Date.now() },
              },
            }));
            return;
          }
          // Roll back the optimistic pending state so the user can retry cleanly.
          setChatState((prev) => {
            if (!prev[stateKey]) return prev;
            const rest = { ...prev };
            delete rest[stateKey];
            return rest;
          });
          setError(errorMsg);
          return;
        } finally {
          pendingCreateByKeyRef.current.delete(pendingStateKey);
        }
      }

      // Optimistic UI: immediately show the user message and a loading indicator.
      // For follow-up messages there can be a delay before startStreaming sets
      // streaming state (e.g. SBS eligibility check over the network). Updating
      // the state here mirrors what the isNewSessionlessChat branch already does
      // for first messages, eliminating the perceived dead time between submit
      // and the first sign of activity.
      const optimisticNow = Date.now();
      const optimisticPlaceholder: ReasoningItemEntry = {
        type: 'reasoning_item',
        id: generateId(),
        content: [],
        position: userPosition + 1,
        backendIndex: -1,
      };
      setChatState((prev) => {
        const existingState = prev[stateKey] ?? {
          timeline: [],
          streamingState: null,
          metadata: { lastActivity: optimisticNow },
        };
        return {
          ...prev,
          [stateKey]: {
            ...existingState,
            timeline: [...timelineWithoutErrors, userMessage, optimisticPlaceholder],
            streamingState: {
              basePosition: userPosition + 1,
              activeIndices: new Map(),
              currentActiveIndex: null,
              activityGroupOpen: true,
              streamStartedAt: optimisticNow,
              lastChunkAt: optimisticNow,
              lastVisibleContentAt: optimisticNow,
            },
            metadata: {
              ...existingState.metadata,
              lastActivity: optimisticNow,
            },
          },
        };
      });

      // Track analytics BEFORE starting the stream so that message_submitted
      // fires before message_received. startStreaming is awaited and only resolves
      // when the stream completes, so placing tracking after the await would cause
      // message_submitted to fire after message_received, breaking funnels.
      trackMessageSubmitted();

      // Track Evidence chat submitted
      lastQueryLengthRef.current = trimmedContent.length;
      // Store chip metadata for message_received enrichment at stream completion.
      // queueDurationMs is captured here (not just at submit) so the matching
      // message_received event can attribute the same queue wait time.
      lastChipMetaRef.current = {
        entryMethod: entryMethod ?? 'typed',
        chipLabel: chipLabel,
        chipCategory: chipCategory,
        chipOrigin: chipOrigin,
        chipSurface: chipSurface,
        queued: queued ?? false,
        queueDurationMs: queueDurationMs,
      };
      trackChatSubmitted({
        query: trimmedContent,
        sources: effectiveDomains,
        chatId: effectiveChatId ?? undefined,
        sessionId: sessionId ?? undefined,
        entryMethod: entryMethod ?? 'typed',
        chipLabel,
        chipCategory,
        chipOrigin,
        chipSurface,
        queued: queued ?? false,
        queueDurationMs,
      });
      appendCheckpoint({
        key: TrackingKey.EVIDENCE_MESSAGE_SEND,
        checkpointName: 'message_submitted',
        metadata: {
          chat_id: effectiveChatId ?? null,
          session_id: sessionId ?? null,
        },
      });

      // Refresh `user_activity.evidence_chat_query` for power-user UI only while the chat
      // banner can still appear — once dismissed, avoid refetching on every message.
      if (
        modeToTrack === ChatMode.EVIDENCE &&
        !isEvidencePowerUserBannerDismissed
      ) {
        void invalidateCurrentUser();
      }

      // Track chat message sent with context (only if has context)
      const hasAttachments =
        (attachmentIds && attachmentIds.length > 0) || (files && files.length > 0);
      const hasLinkedSession = !!attachableLinkedContext.sessionId;
      const hasLinkedPatient = !!attachableLinkedContext.patientProfileId;
      if (hasAttachments || hasLinkedSession || hasLinkedPatient) {
        const attachmentTypes = fileMetadata
          ? fileMetadata.map((f) => getFileExtension(f.name)).filter(Boolean)
          : files?.map((f) => getFileExtension(f.name)).filter(Boolean);

        track({ options: { amplitude: false },
          event_name: TrackingEventName.ChatMessageSentWithContext,
          props: {
            has_linked_session: hasLinkedSession,
            has_linked_patient: hasLinkedPatient,
            has_attachments: !!hasAttachments,
            attachment_count: attachmentIds?.length ?? files?.length ?? 0,
            attachment_types:
              attachmentTypes && attachmentTypes.length > 0 ? attachmentTypes : undefined,
            linked_session_id: attachableLinkedContext.sessionId ?? undefined,
            linked_patient_id: attachableLinkedContext.patientProfileId ?? undefined,
            chat_id: effectiveChatId ?? undefined,
            session_id: sessionId ?? undefined,
            message_length: trimmedContent.length,
            mode: modeToTrack,
          },
        });
      }

      // Always build a source object so analytics has a queued flag on every
      // user message. Chip fields are only included when meaningfully set so
      // we don't persist empty keys. Free-text (no entryMethod) defaults
      // entry_method to 'typed'; FE-ahead / unknown provided values are
      // omitted — never mislabeled as typed — so backend ChatMessageSource
      // validation does not reject the request and we don't rewrite live
      // attribution (e.g. check_other_sources).
      const normalizedQueueDurationMs = normalizeQueueDurationMs(
        queued ?? false,
        queueDurationMs
      );
      const wireEntryMethod =
        entryMethod === undefined
          ? 'typed'
          : toWireEntryMethod(entryMethod);
      const wireSurface = toWireChipSurface(chipSurface);
      const source: ChatMessageSourcePayload & { queued: boolean } = {
        queued: queued ?? false,
        ...(wireEntryMethod !== undefined && {
          entry_method: wireEntryMethod,
        }),
        ...(chipLabel && { chip_label: chipLabel }),
        ...(chipCategory && { chip_category: chipCategory }),
        ...(chipOrigin && { origin: chipOrigin }),
        ...(wireSurface && { surface: wireSurface }),
        // queue_duration_ms is only meaningful for queued dispatches.
        ...(normalizedQueueDurationMs !== undefined && {
          queue_duration_ms: normalizedQueueDurationMs,
        }),
      };

      await startStreaming(
        [...timelineWithoutErrors, userMessage],
        overrideMode,
        (err) => {
          setError(err);
        },
        chatId ?? inFlightClientChatId,
        files, // Legacy: raw files (deprecated)
        attachmentIds, // Pre-uploaded attachment IDs
        source
      );
    },
    [
      conversationTimeline,
      startStreaming,
      analyticsSessionId,
      effectiveMode,
      chatId,
      stateKey,
      setChatState,
      createChat,
      isSessionless,
      trackChatSubmitted,
      trackChatCreated,
      effectiveDomains,
      sessionId,
      store,
      isProvenanceGateKilled,
      isEvidencePowerUserBannerDismissed,
      setError,
      lastQueryLengthRef,
      lastChipMetaRef,
      stayOnPage,
      onHostedChatCreated,
    ]
  );

  return { sendMessage };
}
